/**
 * <review-cards> (sections/review-cards.liquid)
 *
 * - Arrows scroll the card row by one view and are disabled at either end.
 *   Swipe, trackpad and mouse drag come from the native scroller and
 *   <drag-scroller>, so the row still works if this script fails.
 * - Cards whose title or text is cut off get a "Read more" button that opens
 *   the full review in the section's <dialog>.
 * - Any link to the section's anchor (e.g. <a href="#customer-reviews">) on
 *   the same page scrolls smoothly to the section. Links from other pages
 *   (/pages/x#customer-reviews) use the browser's normal anchor jump.
 */
// Block scope: two sections can each load this file on one page; a top-level
// class would then throw "already declared" the second time.
{
  class ReviewCards extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.review-cards__track');
      this.prev = this.querySelector('[data-review-cards-prev]');
      this.next = this.querySelector('[data-review-cards-next]');
      if (!this.track) return;

      this.dialog = this.querySelector('[data-review-cards-dialog]');
      this.dialogBody = this.querySelector('[data-review-cards-dialog-body]');

      this.onPrev = () => this.scrollByView(-1);
      this.onNext = () => this.scrollByView(1);
      this.onScroll = () => this.updateArrows();
      this.onResize = () => {
        this.updateArrows();
        this.updateReadMore();
      };
      this.onTrackClick = (event) => {
        const button = event.target.closest('[data-review-card-more]');
        if (button) this.openReview(button.closest('.review-card'), button);
      };
      this.onDialogClick = (event) => {
        // A click on the backdrop lands on the <dialog> itself.
        if (event.target === this.dialog || event.target.closest('[data-review-cards-close]')) this.dialog.close();
      };
      this.onDialogClose = () => this.lastOpener?.focus();

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('click', this.onTrackClick);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
      this.onResize();
      // Web fonts can change line breaks after first paint.
      document.fonts?.ready.then(() => this.updateReadMore());
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('click', this.onTrackClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.resizeObserver?.disconnect();
    }

    /* Show "Read more" only on cards where the clamp actually hides something. */
    updateReadMore() {
      this.track.querySelectorAll('.review-card').forEach((card) => {
        const title = card.querySelector('.review-card__title');
        const text = card.querySelector('.review-card__text');
        const cut = (title && title.scrollWidth > title.clientWidth + 1) || (text && text.scrollHeight > text.clientHeight + 1);
        const button = card.querySelector('[data-review-card-more]');
        if (button) button.hidden = !cut;
      });
    }

    openReview(card, opener) {
      if (!card || !this.dialog || !this.dialogBody) return;
      const full = card.cloneNode(true);
      full.classList.add('review-card--full');
      full.querySelector('[data-review-card-more]')?.remove();
      this.dialogBody.replaceChildren(full);
      this.lastOpener = opener;
      this.dialog.showModal();
    }

    scrollByView(direction) {
      const slide = this.track.querySelector('.review-cards__slide');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = slide ? slide.offsetWidth + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 1;
    }
  }

  if (!customElements.get('review-cards')) {
    customElements.define('review-cards', ReviewCards);

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link || link.pathname !== window.location.pathname) return;
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id && document.getElementById(id);
      if (!target || target.tagName !== 'REVIEW-CARDS') return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `#${id}`);
    });
  }
}
